import readXlsxFile, {type Row} from 'read-excel-file'

import {CostClassification, RiaPriority, type CreateRiaRequest} from '../types'

export type ParseError = {
  row: number
  field: string
  message: string
  messageParams?: Record<string, string | number>
  severity?: 'error' | 'warning'
}

export type ParseResult = {
  validRows: CreateRiaRequest[]
  errors: ParseError[]
}

const RI_NUMBER_REGEX = /^\d{4}-\d{1,3}$/

const PRIORITY_MAP: Record<string, RiaPriority> = {
  a: RiaPriority.A,
  b: RiaPriority.B,
  c: RiaPriority.C,
  'a - high priority': RiaPriority.A,
  'b - medium priority': RiaPriority.B,
  'c - low priority': RiaPriority.C
}

const COST_CLASSIFICATION_MAP: Record<string, CostClassification> = {
  operationalcosts: CostClassification.OperationalCosts,
  capitalexpenditure: CostClassification.CapitalExpenditure,
  humanelementlowcosts: CostClassification.HumanElementLowCosts,
  'operational costs': CostClassification.OperationalCosts,
  'operational cost': CostClassification.OperationalCosts,
  'capital expenditure': CostClassification.CapitalExpenditure,
  'human element / low costs': CostClassification.HumanElementLowCosts,
  'human element/low costs': CostClassification.HumanElementLowCosts
}

const COLUMN_MAP: Record<string, string> = {
  'ri number': 'riNumber',
  'ri no': 'riNumber',
  'ri no.': 'riNumber',
  'ri#': 'riNumber',
  'ri #': 'riNumber',
  'date issued': 'dateIssued',
  date: 'dateIssued',
  title: 'title',
  recommendation: 'title',
  priority: 'priority',
  'cost classification': 'costClassification',
  cost: 'costClassification',
  'indicative time scale': 'indicativeTimeScale',
  timescale: 'indicativeTimeScale',
  'le prior': 'lePrior',
  'le after': 'leAfter'
}

const SHEET_NAME = 'RI Response Form'

const normalizeCell = (value: unknown): string =>
  String(value ?? '')
    .replace(/[\s\u00A0]+/g, ' ')
    .toLowerCase()
    .trim()

const findHeaderRowIndex = (rows: Row[]): number => {
  for (let i = 0; i < rows.length; i++) {
    const row = rows[i]
    const cellTexts = row.map(normalizeCell)
    const matchCount = cellTexts.filter((t) => COLUMN_MAP[t] !== undefined).length
    if (matchCount >= 3) return i
  }
  return -1
}

const mapColumnIndices = (headerRow: Row): Record<string, number> => {
  const indices: Record<string, number> = {}
  headerRow.forEach((cell, idx) => {
    const key = normalizeCell(cell)
    const field = COLUMN_MAP[key]
    if (field && indices[field] === undefined) {
      indices[field] = idx
    }
  })
  return indices
}

const parseNumericCell = (value: unknown): number | null => {
  if (value === null || value === undefined || value === '') return null
  const str = String(value).trim().toLowerCase()
  if (str === 'n.a.' || str === 'n/a' || str === '-') return null
  const normalized = typeof value === 'string' ? value.replace(/,/g, '').trim() : value
  const num = Number(normalized)
  return isNaN(num) ? null : num
}

const EXCEL_EPOCH = new Date(Date.UTC(1899, 11, 30))

const excelSerialToDate = (serial: number): string => {
  const ms = EXCEL_EPOCH.getTime() + serial * 86400000
  return new Date(ms).toISOString().split('T')[0]
}

const parseDateCell = (value: unknown): string | null => {
  if (value instanceof Date) {
    return value.toISOString().split('T')[0]
  }
  if (typeof value === 'number') {
    return excelSerialToDate(value)
  }
  if (typeof value === 'string' && value.trim()) {
    const trimmedValue = value.trim()

    if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedValue)) {
      return trimmedValue
    }

    const parsedDate = new Date(trimmedValue)
    if (!Number.isNaN(parsedDate.getTime())) {
      return parsedDate.toISOString().split('T')[0]
    }
  }
  return null
}

export const parseExcelFile = async (file: File): Promise<ParseResult> => {
  let rows: Row[]
  try {
    rows = await readXlsxFile(file, {sheet: SHEET_NAME})
  } catch {
    rows = await readXlsxFile(file)
  }
  return parseRows(rows)
}

type CellGetter = (field: string) => unknown

const validateRiNumber = (
  getCellValue: CellGetter,
  rowNum: number,
  seenRiNumbers: Set<string>
): {riNumber: string; errors: ParseError[]; skip: boolean} => {
  const errors: ParseError[] = []
  const riNumberRaw = String(getCellValue('riNumber') ?? '').trim()

  if (!riNumberRaw || normalizeCell(riNumberRaw) === 'total') {
    return {riNumber: '', errors: [], skip: true}
  }

  if (!RI_NUMBER_REGEX.test(riNumberRaw)) {
    errors.push({row: rowNum, field: 'riNumber', message: 'validation.riNumberFormat'})
  }

  const riNumber = riNumberRaw.replace(
    /^(\d{4})-(\d{1,2})$/,
    (_, y, n) => `${y}-${n.padStart(3, '0')}`
  )

  if (seenRiNumbers.has(riNumber)) {
    errors.push({
      row: rowNum,
      field: 'riNumber',
      message: 'validation.duplicateRiNumber',
      messageParams: {riNumber}
    })
  }
  seenRiNumbers.add(riNumber)

  return {riNumber, errors, skip: false}
}

const validateDateIssued = (
  getCellValue: CellGetter,
  rowNum: number
): {dateIssued: string | null; errors: ParseError[]} => {
  const dateIssuedRaw = getCellValue('dateIssued')
  const dateIssued = parseDateCell(dateIssuedRaw)

  if (!dateIssued) {
    return {
      dateIssued: null,
      errors: [
        {
          row: rowNum,
          field: 'dateIssued',
          message:
            String(dateIssuedRaw ?? '').trim().length > 0
              ? 'validation.dateInvalid'
              : 'validation.dateRequired'
        }
      ]
    }
  }

  return {dateIssued, errors: []}
}

const validateTitle = (
  getCellValue: CellGetter,
  rowNum: number
): {title: string; errors: ParseError[]} => {
  const title = String(getCellValue('title') ?? '').trim()

  if (!title) {
    return {title: '', errors: [{row: rowNum, field: 'title', message: 'validation.titleRequired'}]}
  }

  return {title, errors: []}
}

const validatePriority = (
  getCellValue: CellGetter,
  rowNum: number
): {priority: RiaPriority | undefined; errors: ParseError[]} => {
  const priorityRaw = String(getCellValue('priority') ?? '').trim()
  const priority = PRIORITY_MAP[priorityRaw.toLowerCase()]

  if (!priorityRaw) {
    return {
      priority: undefined,
      errors: [{row: rowNum, field: 'priority', message: 'validation.priorityRequired'}]
    }
  }

  if (!priority) {
    return {
      priority: undefined,
      errors: [
        {
          row: rowNum,
          field: 'priority',
          message: 'validation.priorityInvalid',
          messageParams: {value: priorityRaw}
        }
      ]
    }
  }

  return {priority, errors: []}
}

const validateCostClassification = (
  getCellValue: CellGetter,
  rowNum: number
): {costClassification: CostClassification | undefined; errors: ParseError[]} => {
  const costRaw = String(getCellValue('costClassification') ?? '').trim()
  const costClassification = COST_CLASSIFICATION_MAP[costRaw.toLowerCase()]

  if (!costRaw) {
    return {
      costClassification: undefined,
      errors: [
        {
          row: rowNum,
          field: 'costClassification',
          message: 'validation.costClassificationRequired'
        }
      ]
    }
  }

  if (!costClassification) {
    return {
      costClassification: undefined,
      errors: [
        {
          row: rowNum,
          field: 'costClassification',
          message: 'validation.costClassificationInvalid',
          messageParams: {value: costRaw}
        }
      ]
    }
  }

  return {costClassification, errors: []}
}

const parseIndicativeTimeScale = (
  getCellValue: CellGetter
): {indicativeTimeScale: string | null; isText: boolean} => {
  const raw = getCellValue('indicativeTimeScale')

  if (raw instanceof Date) {
    return {indicativeTimeScale: raw.toISOString().split('T')[0], isText: false}
  }
  if (typeof raw === 'number') {
    return {indicativeTimeScale: excelSerialToDate(raw), isText: false}
  }

  const trimmed = String(raw ?? '').trim()
  const isText = !!trimmed && isNaN(Date.parse(trimmed))
  return {indicativeTimeScale: trimmed || null, isText}
}

export const parseRows = (rows: Row[]): ParseResult => {
  const errors: ParseError[] = []
  const validRows: CreateRiaRequest[] = []

  const headerIdx = findHeaderRowIndex(rows)
  if (headerIdx === -1) {
    errors.push({row: 0, field: '', message: 'validation.headerNotFound'})
    return {validRows, errors}
  }

  const columnIndices = mapColumnIndices(rows[headerIdx])
  const seenRiNumbers = new Set<string>()

  for (let i = headerIdx + 1; i < rows.length; i++) {
    const row = rows[i]
    const rowNum = i + 1

    const isBlank = row.every(
      (cell) => cell === null || cell === undefined || String(cell).trim() === ''
    )
    if (isBlank) continue

    const getCellValue: CellGetter = (field) => {
      const idx = columnIndices[field]
      return idx !== undefined ? row[idx] : undefined
    }

    const ri = validateRiNumber(getCellValue, rowNum, seenRiNumbers)
    if (ri.skip) continue

    const date = validateDateIssued(getCellValue, rowNum)
    const titleResult = validateTitle(getCellValue, rowNum)
    const priorityResult = validatePriority(getCellValue, rowNum)
    const costResult = validateCostClassification(getCellValue, rowNum)

    const rowErrors = [
      ...ri.errors,
      ...date.errors,
      ...titleResult.errors,
      ...priorityResult.errors,
      ...costResult.errors
    ]

    const {indicativeTimeScale, isText: indicativeTimeScaleIsText} =
      parseIndicativeTimeScale(getCellValue)
    const lePrior = parseNumericCell(getCellValue('lePrior'))
    const leAfter = parseNumericCell(getCellValue('leAfter'))

    if (rowErrors.length > 0) {
      errors.push(...rowErrors)
      continue
    }

    if (indicativeTimeScaleIsText && indicativeTimeScale) {
      errors.push({
        row: rowNum,
        field: 'indicativeTimeScale',
        message: 'validation.indicativeTimeScaleNotDate',
        messageParams: {value: indicativeTimeScale},
        severity: 'warning'
      })
    }

    const dateIssued = date.dateIssued
    const priority = priorityResult.priority
    const costClassification = costResult.costClassification
    if (!dateIssued || !priority || !costClassification) {
      continue
    }

    validRows.push({
      riNumber: ri.riNumber,
      dateIssued,
      title: titleResult.title,
      priority,
      costClassification,
      indicativeTimeScale: indicativeTimeScaleIsText ? null : indicativeTimeScale,
      lePrior,
      leAfter
    })
  }

  return {validRows, errors}
}
